import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Card } from '../../../../../components';
import {
    CallToActionNullState,
    Dialog,
    DialogContent,
    IconButton,
} from '../../../../../compositions';
import { closeDialog, openDialog } from '../../../../../helpers/dialog';
import trans from '../../../../../helpers/trans';
import { SkillBase, SkillsFormData } from '../../../../../models/@competentNL/Skills/Skills';
import { OrganisationOccupation } from '../../../../../models/OrganisationOccupation/OrganisationOccupation';
import { FormOption } from '../../../../../types';
import { SkillsForm } from '../../../..';

import './OccupationSkillsCard.scss';

interface OccupationSkillsCardProps {
    suggestionIsLoading?: boolean;
    isUpdating: boolean;
    isSuccessful: boolean;
    occupation: OrganisationOccupation;
    searchedSkills: SkillBase[];
    suggestedSkills: SkillBase[];
    skillsError?: string;
    occupationOptions: FormOption[];
    onSkillSearchChange?: (query: string) => void;
    onOccupationSearchChange?: (query: string) => void;
    onOccupationSearchSelect?: (query: string) => void;
    onUpdateSkills: (skillsFormData: SkillsFormData) => void;
    onCloseSkillsDialog: () => void;
    className?: string;
}

const OccupationSkillsCard: FC<OccupationSkillsCardProps> = ({
    suggestionIsLoading,
    isSuccessful,
    occupation,
    searchedSkills,
    suggestedSkills,
    skillsError,
    occupationOptions,
    onSkillSearchChange,
    onOccupationSearchChange,
    onOccupationSearchSelect,
    onUpdateSkills,
    onCloseSkillsDialog,
    className = '',
}): ReactElement => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const [skillsFormKey, setSkillsFormKey] = useState<number>(0);

    const handleOpenDialog = (): void => openDialog(dialogRef);
    const handleCloseDialog = (): void => {
        closeDialog(dialogRef);
        onCloseSkillsDialog();

        setSkillsFormKey(skillsFormKey + 1);
    };

    const { skills } = occupation;

    useEffect((): void => {
        if (isSuccessful) handleCloseDialog();
    }, [isSuccessful]);

    const skillsFormTitleKey = useMemo((): string => (
        skills.length === 0 ? 'addTitle' : 'editTitle'
    ), [skills]);

    return (
        <Card className={`occupation-skills-card ${className}`}>
            <header className="occupation-skills-card__header">
                <h3 className="occupation-skills-card__title">
                    {trans('common.skills')}
                </h3>

                {skills.length > 0 && (
                    <IconButton
                        icon="plus"
                        text={trans('containers.organisationDetailOccupation.actions.addSkills')}
                        onClick={handleOpenDialog}
                        className="occupation-skills-card__add-button"
                    />
                )}
            </header>

            {skills.length === 0 && (
                <CallToActionNullState
                    isEditable
                    icon="skills-passport"
                    paragraph={trans('containers.organisationDetailOccupation.skillsNullState.paragraph')}
                    subParagraph={trans('containers.organisationDetailOccupation.skillsNullState.subParagraph')}
                    buttonText={trans('containers.organisationDetailOccupation.skillsNullState.callToAction')}
                    onButtonClick={handleOpenDialog}
                    className="occupation-skills-card__null-state"
                />
            )}

            {skills.length > 0 && (
                <ul className="occupation-skills-card__list">
                    {skills.map(skill => (
                        <li key={skill.id} className="occupation-skills-card__list-item">
                            {skill.label}
                        </li>
                    ))}
                </ul>
            )}

            <Dialog ref={dialogRef} onClose={handleCloseDialog}>
                <DialogContent
                    title={trans(`containers.organisationDetailOccupation.skillsForm.${skillsFormTitleKey}`)}
                >
                    <SkillsForm
                        key={skillsFormKey}
                        showSearch
                        showOccupationSearch
                        isLoading={suggestionIsLoading}
                        activeSkills={skills}
                        searchedSkillOptions={searchedSkills}
                        occupationOptions={occupationOptions}
                        suggestedSkillOptions={suggestedSkills}
                        error={skillsError}
                        onSkillSearchChange={onSkillSearchChange}
                        onOccupationSearchChange={onOccupationSearchChange}
                        onOccupationSearchSelect={onOccupationSearchSelect}
                        onSubmit={onUpdateSkills}
                        onCancel={handleCloseDialog}
                    />
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default OccupationSkillsCard;
